import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  Document,
  DocumentApi,
  DocumentMetadata,
  DocumentRequest,
  PresignedUrlRequest,
  PresignedUrlResponse,
} from '@via/models';
import { lastValueFrom } from 'rxjs';
import { DocumentDetails } from 'src/app/layout/modules/case-management/pages/cases/pages/documents/documents.model';
import { ApiService } from 'src/app/services/api.service';

@Injectable({
  providedIn: 'root',
})
export class DocumentService {
  constructor(
    private http: HttpClient,
    private router: Router,
    private apiService: ApiService
  ) {}

  /**
   * @description This is used when prepopulating uploaded files
   */
  createDummyFile(fileName: string, fileSizeInBytes: number) {
    const content = new Uint8Array(fileSizeInBytes);
    const blob = new Blob([content]);
    return new File([blob], fileName);
  }

  getDocumentByCaseId(caseId: number) {
    return this.apiService.get<DocumentDetails[]>(
      `viacore/api/documents/${caseId}`
    );
  }
  /**
   * @description Get Documents including metadata by Ids
   */
  getDocumentsByIds(stream: DocumentApi, ids: (string | number)[]) {
    return this.apiService.get<Document[]>(`${stream}/api/documents`, {
      params: {
        ids,
      },
    });
  }

  getPresignedUrl(
    stream: DocumentApi,
    uniqueId: string,
    payload: PresignedUrlRequest
  ) {
    return this.apiService.get<PresignedUrlResponse>(
      `${stream}/api/signed-url`,
      {
        params: {
          uniqueId,
          ...payload,
        },
      }
    );
  }

  async upload(
    stream: DocumentApi,
    document: File,
    payload?: {
      documentType?: string;
      caseNumber?: string;
    },
    fileName?: string
  ) {
    const uniqueId = crypto.randomUUID();
    const presignedUrlRequest: PresignedUrlRequest = {
      fileName: fileName ?? document?.name,
      fileExt: `.${document.name.split('.')[1]}`,
      fileSize: document.size,
      contentType: document.type,
      isUpload: true,
      ...payload,
    };
    const presignedUrlResponse = await this.getPresignedUrl(
      stream,
      uniqueId,
      presignedUrlRequest
    );

    const documentRequest: DocumentRequest = {
      'x-ss-meta-uniqueid': uniqueId,
      'x-ss-meta-file-name': presignedUrlRequest.fileName,
      ContentType: presignedUrlRequest.contentType,
      'Cache-Control': 'max-age=31536000',
    };

    let headers = new HttpHeaders();
    for (const [key, value] of Object.entries(documentRequest)) {
      headers = headers.append(key, value);
    }
    const formData = new FormData();
    formData.append('document', document);

    await lastValueFrom(
      this.http.put(presignedUrlResponse.presignedUrl, formData, {
        headers,
      })
    );

    return presignedUrlResponse.id;
  }

  /**
   * @description Direct file download as Blob
   */
  async download(
    stream: DocumentApi,
    uniqueId: string,
    documentMetadata: DocumentMetadata,
    payload?: {
      documentType?: string;
      caseNumber?: string;
    }
  ) {
    const presignedUrlRequest: PresignedUrlRequest = {
      fileName: documentMetadata.fileName,
      fileExt: documentMetadata.fileExt,
      fileSize: documentMetadata.fileSize,
      contentType: documentMetadata.fileType,
      isUpload: false,
      ...payload,
    };

    const { presignedUrl } = await this.getPresignedUrl(
      stream,
      uniqueId,
      presignedUrlRequest
    );
    const documentRequest: DocumentRequest = {
      'x-ss-meta-uniqueid': uniqueId,
      'x-ss-meta-file-name': presignedUrlRequest.fileName,
      ContentType: presignedUrlRequest.contentType,
      'Cache-Control': 'max-age=31536000',
    };

    const blob = await this.getBlob(presignedUrl, documentRequest);
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    // TODO: content-disposition
    link.download = presignedUrlRequest.fileName;
    link.click();
    URL.revokeObjectURL(url);
  }

  /**
   * @description Get document Blob
   */
  async getBlob(presignedUrl: string, payload: DocumentRequest) {
    const headers = new HttpHeaders();
    for (const [key, value] of Object.entries(payload)) {
      headers.append(key, value);
    }

    const documentResponse = await lastValueFrom(
      this.http.get<File>(presignedUrl, {
        headers,
        observe: 'response',
        responseType: 'blob' as any,
      })
    );

    return new Blob([documentResponse.body as File]);
  }

  /**
   * @description Get Blob using document metadata
   */
  async getBlobUsingMetadata(
    stream: DocumentApi,
    uniqueId: string,
    documentMetadata: DocumentMetadata,
    payload?: {
      documentType?: string;
      caseNumber?: string;
    }
  ) {
    const presignedUrlRequest: PresignedUrlRequest = {
      fileName: documentMetadata.fileName,
      fileExt: documentMetadata.fileExt,
      fileSize: documentMetadata.fileSize,
      contentType: documentMetadata.fileType,
      isUpload: false,
      ...payload,
    };
    const { presignedUrl } = await this.getPresignedUrl(
      stream,
      uniqueId,
      presignedUrlRequest
    );
    const documentRequest: DocumentRequest = {
      'x-ss-meta-uniqueid': uniqueId,
      'x-ss-meta-file-name': presignedUrlRequest.fileName,
      ContentType: presignedUrlRequest.contentType,
      'Cache-Control': 'max-age=31536000',
    };
    return this.getBlob(presignedUrl, documentRequest);
  }

  convertDocumentToHtml(
    stream: DocumentApi,
    payload: {
      uniqueId: string;
      documentMetadata: DocumentMetadata;
      caseNumber?: string;
    }
  ) {
    return this.apiService.post<string>(`${stream}/api/convert-html`, payload, {
      responseType: 'text',
    });
  }

  getDocumentViewerUrl(
    stream: DocumentApi,
    uniqueId: string,
    documentName: string,
    queryParams?: object
  ) {
    const serializedUrl = this.router.serializeUrl(
      this.router.createUrlTree(
        ['document-viewer', stream, uniqueId, documentName],
        { queryParams }
      )
    );
    return `#${serializedUrl}`;
  }

  createAndDownloadWordFile(file: any, fileName: string) {
    // convert file of response to word file
    const byteCharacters = atob(file.fileContents);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const byteArray = new Uint8Array(byteNumbers);
    const blob = new Blob([byteArray], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${fileName}.docx`;
    link.click();
    URL.revokeObjectURL(url);
  }
}
